/**
 * iCal calendar types — shared across the calendar feature.
 *
 * Time spaces (see zonedTime.ts): a parsed event is stored in its SERIES SPACE - real UTC instants
 * for zoned/UTC times, the written wall clock as pseudo-UTC for floating times and all-day dates.
 * Expanded occurrences are always real instants on the viewer's calendar.
 */

// ---------------------------------------------------------------------------
// Calendar config (stored in Dexie `#ical-calendars`, synced)
// ---------------------------------------------------------------------------

export interface ICalCalendar {
  id: string
  url: string
  name: string
  color: string
  /** Set when the add flow fetched and parsed the feed. Informational: every config with a URL is fetched. */
  validated: boolean
}

/**
 * v1 stored `ExternalICalendars[]` in `#ical-calendars` with `link` + `valid`.
 * v2 uses `ICalCalendar` with `url` + `validated`.
 * This normalizer handles both formats so synced v1 data works.
 */
export function normalizeCalendarConfig(
  raw: Record<string, unknown>,
): ICalCalendar {
  return {
    id: (raw.id as string) ?? crypto.randomUUID(),
    url: (raw.url as string) ?? (raw.link as string) ?? "",
    name: (raw.name as string) ?? "",
    color: (raw.color as string) ?? "#3b82f6",
    validated:
      typeof raw.validated === "boolean"
        ? raw.validated
        : typeof raw.valid === "boolean"
          ? raw.valid
          : false,
  }
}

/** Normalize an array of potentially mixed v1/v2 calendar configs. */
export function normalizeCalendarConfigs(items: unknown): ICalCalendar[] {
  if (!Array.isArray(items)) return []
  return items.map((item) =>
    normalizeCalendarConfig(item as Record<string, unknown>),
  )
}

// ---------------------------------------------------------------------------
// Per-calendar status (runtime + cache in `#ical-calendars-data`, device-local)
// ---------------------------------------------------------------------------

export type CalendarErrorCode =
  /** The request failed before an answer (offline, proxy unreachable, client timeout). */
  | "network"
  /** 400: the URL was refused (invalid, too long, not HTTPS, or a private/blocked address). */
  | "invalid-url"
  /** 401: the session is not signed in. */
  | "auth"
  /** 413: the feed is larger than the proxy accepts (5 MB). */
  | "too-large"
  /** 429: too many calendar requests; retried on the next refresh. */
  | "rate-limited"
  /** 502: the calendar server answered with an error or redirected too often. */
  | "upstream"
  /** 504: the calendar server took too long. */
  | "timeout"
  /** Any other error status. */
  | "http"
  /** The response is not an iCalendar feed (an HTML login page, for instance). */
  | "not-ical"
  /** The feed could not be parsed at all. */
  | "parse"
  /** The feed parsed but none of its events could be read. */
  | "no-readable-events"

export interface CalendarError {
  code: CalendarErrorCode
  /** HTTP status for `http`. */
  status?: number
  /** ms */
  at: number
}

export interface CalendarStatus {
  /** ms of the last fetch that produced the data shown. */
  lastOk?: number
  /** The last attempt's failure; cleared by the next success. The previous data is kept. */
  lastError?: CalendarError
  /** Events (a series counts once) in the data shown. */
  eventCount: number
  /** Recurring series in the data shown. */
  recurringCount: number
  /** Events the parser had to skip, with reasons. */
  skipped: SkippedEvent[]
  /** Distinct TZIDs whose zone is a guess (shown in the calendar's or the viewer's zone). */
  uncertainZones: string[]
}

// ---------------------------------------------------------------------------
// Parsed iCal events
// ---------------------------------------------------------------------------

export type SkipReason =
  /** The VEVENT block is not valid iCalendar syntax. */
  | "unparseable"
  /** No DTSTART. */
  | "no-start"
  /** A date or time that could not be read. */
  | "invalid-date"
  /** The RRULE could not be read: the event is shown once, without its recurrence. */
  | "invalid-rrule"
  /** Anything else that threw while reading the event. */
  | "error"

export interface SkippedEvent {
  uid?: string
  summary?: string
  reason: SkipReason
}

export interface ICalEventItem {
  uid: string
  summary: string
  description?: string | null
  organizer?: string | null
  attendee_count?: number
  location?: string | null
  /**
   * Unix seconds in series space: the instant for zoned/UTC events, the wall clock as pseudo-UTC
   * for floating events and all-day dates (00:00 of the first day).
   */
  start: number
  /** Unix seconds in series space; exclusive (all-day: 00:00 of the day after the last day). */
  end: number
  /** The written DTSTART wall clock (pseudo-UTC seconds) of a zoned event - recurrence runs on it. */
  wallStart?: number
  isOccurring?: boolean
  /** RRULE value without UNTIL ("FREQ=WEEKLY;BYDAY=MO"); see `rruleUntil`. */
  rrule?: string
  /** UNTIL as wall clock (pseudo-UTC seconds) in the series' zone, inclusive. */
  rruleUntil?: number
  /** Excluded starts (EXDATE, overridden and cancelled RECURRENCE-IDs), series-space seconds. */
  exdates?: number[]
  /** Excluded days (DATE-valued exclusions, and every exclusion of an all-day series), `YYYY-MM-DD`. */
  exdateDays?: string[]
  /** Extra starts (RDATE), series-space seconds. */
  rdates?: number[]
  /** Set on a modified instance (RECURRENCE-ID): series-space seconds of the instance it replaces. */
  recurrenceId?: number
  /** Resolved IANA zone of a zoned/UTC event ("UTC" for UTC). Absent for floating and all-day. */
  originalTzid?: string
  /** The zone is a guess (unknown TZID): the event is shown in the calendar's or viewer's zone. */
  tzUncertain?: boolean
  allDay?: boolean
  multiDay?: boolean
  isFloating?: boolean
  calendarId?: string
}

// ---------------------------------------------------------------------------
// Expanded occurrences (one per calendar day)
// ---------------------------------------------------------------------------

export interface EventOccurrence extends ICalEventItem {
  /** YYYY-MM-DD (viewer's local day) */
  occurrenceDate: string
  /** Unix seconds (a real instant, also for floating and all-day events) */
  occurrenceStart: number
  /** Unix seconds (a real instant; all-day: the last second of the last day) */
  occurrenceEnd: number
  color?: string
  occurrences?: number
}
